import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  styled,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useWatch, useFormContext } from 'react-hook-form';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';

// ─── Layout helpers ───────────────────────────────────────────────────────────
const Row = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  gap: '20px',
}));

const Col = styled('div')(() => ({
  flex: 1,
}));

// ─── Per-type option lists for SMS ───────────────────────────────────────────
// Submitted (S) is NOT in adjustedTo.
// Pending (P) is supported in adjustedTo for Delivered and Failed.
// DND is included in adjustedTo of Delivered and Failed.
const ADJUSTED_OPTIONS = {
  delivered: [
    { label: 'Pending (P)', value: 'P' },
    { label: 'Failed (F)', value: 'F' },
    { label: 'DND', value: 'DND' },
  ],
  failed: [
    { label: 'Pending (P)', value: 'P' },
    { label: 'Delivered (D)', value: 'D' },
    { label: 'DND', value: 'DND' },
  ],
};

const TYPE_CONFIG = {
  delivered: {
    label: 'Delivered (D)',
    countField: 'deliveredCount',
    adjustedField: 'adjustedDeliveredCount',
  },
  submitted: {
    label: 'Submitted (S)',
    countField: 'submittedCount',
  },
  failed: {
    label: 'Failed (F)',
    countField: 'failedCount',
    adjustedField: 'adjustedFailedCount',
  },
};

// ─── Single count-type section ────────────────────────────────────────────────
function CountSection({ type, countMode }) {
  const { control } = useFormContext();
  const config = TYPE_CONFIG[type];
  if (!config) return null;

  const { label, countField, adjustedField } = config;
  const options = ADJUSTED_OPTIONS[type] || [];
  const isPercentage = countMode === 'percent';
  const selectedAdjusted = useWatch({ control, name: adjustedField }) || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {type === 'submitted' ? (
        // Submitted Count Update: No "Adjusted To" field
        <Row>
          <Col>
            <RHFTextField
              name={countField}
              label={`${label} ${isPercentage ? 'Target Percentage (%)' : 'Target Count'}`}
              type="number"
              fullWidth
              helperText="Submitted count can only be increased (cannot exceed actual campaign contacts)"
              inputProps={{ min: 0, max: isPercentage ? 100 : undefined }}
            />
          </Col>
          <Col />
          <Col />
        </Row>
      ) : (
        // Delivered, Failed, DND Count Update: Includes Adjusted From (e.g. Failed, DND)
        <Row>
          <Col>
            <RHFTextField
              name={countField}
              label={`${label} ${isPercentage ? 'Percentage (%)' : 'Count'}`}
              type="number"
              fullWidth
              inputProps={{ min: 0, max: isPercentage ? 100 : undefined }}
            />
          </Col>
          <Col>
            <RHFSelect
              name={adjustedField}
              label={`Adjusted From (${label})`}
              size="medium"
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  const labels = (selected || []).map(
                    (v) => options.find((o) => o.value === v)?.label
                  );
                  return labels.length > 2
                    ? `${labels.slice(0, 2).join(', ')} (+${labels.length - 2} More)`
                    : labels.join(', ');
                },
              }}
            >
              {options.map(({ label: optLabel, value }) => (
                <MenuItem key={value} value={value}>
                  <Checkbox checked={selectedAdjusted.includes(value)} size="small" />
                  <ListItemText primary={optLabel} />
                </MenuItem>
              ))}
            </RHFSelect>
          </Col>
          <Col />
        </Row>
      )}

      {/* Special Breakdown for Submitted Count (S) Increase: Specify Delivered, Failed, DND */}
      {type === 'submitted' && (
        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: 1,
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'),
            border: (theme) => `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Status Distribution for Increased Contacts (Delivered, Failed, DND)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Specify how many of the newly added submitted contacts should be Delivered, Failed, or DND:
          </Typography>

          <Row>
            <Col>
              <RHFTextField
                name="submittedDeliveredCount"
                label={`Delivered ${isPercentage ? '(%)' : 'Count'}`}
                type="number"
                fullWidth
                inputProps={{ min: 0, max: isPercentage ? 100 : undefined }}
              />
            </Col>
            <Col>
              <RHFTextField
                name="submittedFailedCount"
                label={`Failed ${isPercentage ? '(%)' : 'Count'}`}
                type="number"
                fullWidth
                inputProps={{ min: 0, max: isPercentage ? 100 : undefined }}
              />
            </Col>
            <Col>
              <RHFTextField
                name="submittedDndCount"
                label={`DND ${isPercentage ? '(%)' : 'Count'}`}
                type="number"
                fullWidth
                inputProps={{ min: 0, max: isPercentage ? 100 : undefined }}
              />
            </Col>
          </Row>
        </Box>
      )}
    </Box>
  );
}

// ─── Combined card ────────────────────────────────────────────────────────────
export default function SmsCountUpdateCard({ actionType, countMode }) {
  const config = TYPE_CONFIG[actionType];
  if (!config) return null;

  return (
    <Box
      sx={{
        mt: 2,
        p: 3,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        SMS {config.label} Count Update
      </Typography>

      <Box sx={{ mb: 1 }}>
        <CountSection type={actionType} countMode={countMode} />
      </Box>
    </Box>
  );
}

SmsCountUpdateCard.propTypes = {
  actionType: PropTypes.string.isRequired,
  countMode: PropTypes.oneOf(['count', 'percent']).isRequired,
};

CountSection.propTypes = {
  type: PropTypes.string.isRequired,
  countMode: PropTypes.oneOf(['count', 'percent']).isRequired,
};
